/**
 * Source of truth for the JOINED blueprint-extraction output. One AI call sees
 * EVERY blueprint file/segment that maps to one physical building (grouped by
 * classifier `building_instance_id`, with all unidentified blueprint segments
 * joined into one "ungrouped" group). The goal is the overall building
 * VOLUME — per-segment extractors handle precise per-storey/per-edge numbers.
 *
 * Output is stored on the batch's aggregated output as
 * `joinedBlueprintExtractions[]` and fed into the merger as additional
 * snapshots so the design AI's `description_for_design_ai` is anchored on
 * volume-accurate prose.
 */

import { z } from 'zod';
import { STOREY_STACK_EVIDENCE_KINDS } from '../models/project-file-batch.model';
import { SPECIAL_STOREY_KINDS } from '../models/storey-level.model';
import {
  LEVELS_WITHIN_STOREY_DESCRIPTION,
  STOREY_INDEX_FROM_ENTRANCE,
} from './project-file-extraction-json-schema.helpers';

const nullableStr = (description: string) =>
  z.nullable(z.string()).describe(description);

const nullableNum = (description: string) =>
  z.nullable(z.number()).describe(description);

const stripJsonSchemaMeta = (schema: z.ZodType): Record<string, unknown> => {
  const { $schema: _ignored, ...rest } = z.toJSONSchema(schema) as Record<
    string,
    unknown
  > & { $schema?: string };
  return rest;
};

/** Storey kinds a stack row can have. The roof and a typical plan are not floors of their own. */
const JOINED_STOREY_STACK_KINDS = ['regular', ...SPECIAL_STOREY_KINDS] as const;

export type JoinedStoreyStackKind = (typeof JOINED_STOREY_STACK_KINDS)[number];

const joinedStoreyStackRowSchema = z.object({
  part_name: nullableStr(
    'Name printed for the piece of the building this floor belongs to, when the documents describe the building in named pieces with their own stacks. Null otherwise.',
  ),
  index: z
    .number()
    .int()
    .nullable()
    .describe(
      `${STOREY_INDEX_FROM_ENTRANCE} A printed plan number is not the index. Null when the floor cannot be placed.`,
    ),
  kind: z
    .enum(JOINED_STOREY_STACK_KINDS)
    .describe(
      'regular for a full storey. attic for vind/vindsplan/vindsvåning. technical for a teknikvåning or fläktrum floor. mezzanine for an entresol. split_level for a suterräng floor partly below grade.',
    ),
  names: z
    .array(z.string())
    .describe(
      'Every name any sheet prints for this floor, as printed, e.g. ["Plan 17", "Vindsplan"]. Empty when no sheet names it.',
    ),
  plan_numbers: z
    .array(z.number().int())
    .describe(
      'Plan numbers printed for this floor on any sheet. Empty when none is printed. Never a number no sheet prints.',
    ),
  floor_level_m: nullableNum(
    'Level in metres printed at the finished floor of this storey on a section or elevation, as printed. Null when none is printed.',
  ),
  levels_within_m: z
    .array(z.number())
    .describe(LEVELS_WITHIN_STOREY_DESCRIPTION),
  plan_drawing_refs: z
    .array(z.string())
    .describe(
      'Refs from the drawing list in the prompt of the plan drawings that show this floor. A sheet that stands for several floors is listed on each. Empty when no listed plan shows it.',
    ),
  evidence: z
    .array(
      z.object({
        kind: z
          .enum(STOREY_STACK_EVIDENCE_KINDS)
          .describe('What shows this floor and its level.'),
        file_name: z.string().describe('File name as given in the prompt.'),
        page: z
          .number()
          .int()
          .nullable()
          .describe('1-based page in that file; null when not known.'),
      }),
    )
    .describe(
      "The sheets that show this floor in this building: this building's own section or elevation showing its level, its own plan, or an area table row. At least one row.",
    ),
});

export const joinedBlueprintBuildingSchema = z.object({
  building_instance_key: nullableStr(
    'Echo of the classifier `building_instance_id` for the group; null for the synthetic "ungrouped" group covering blueprint segments without an id.',
  ),
  building_label: nullableStr(
    'Human label from the document (Hus A, Hus 2 etc.) when consistent across the contributing sheets; null if not stated.',
  ),
  source_file_names: z
    .array(z.string())
    .describe(
      'Filenames of every contributing file. Echo back exactly the names supplied in the prompt context.',
    ),
  accurate_description: z
    .string()
    .describe(
      [
        'Required. Prose describing the BUILDING VOLUME bottom-up, storey by storey, for the downstream design AI.',
        'INCLUDE: total height, total storey count, and every storey whose footprint differs from the others — which storey, and how the outline differs (set-back, overhang, or a second mass). When a storey contains several distinct masses, list each mass. Storeys that share the same footprint can be described once together. Also describe how the masses relate vertically (set-backs, overhangs, step-backs) and roof form when readable. Do not state a length or a depth.',
        'EXCLUDE: apartment programmes, +RH2000 / sea-level elevations, addresses, materials/finishes, regulatory references, precise per-storey numbers below cm precision (those belong in per-segment extractions).',
        'Swedish welcome when sources are Swedish.',
      ].join('\n'),
    ),
  overall_height_m: nullableNum(
    'Approximate total height (ground to roof/parapet) in meters. Null when not inferable.',
  ),
  storey_stack: z
    .array(joinedStoreyStackRowSchema)
    .describe(
      'Every floor plate of this building, bottom to top, one row per floor as the sections, elevations and plans show it together. A floor named differently on different sheets is one row. A typical plan is one row for each floor it stands for. A roof plan with no rooms is not a row. Empty when the attachments show no floor.',
    ),
  storey_count: nullableNum(
    'Number of storeys of this building: the distinct `storey_stack` indexes at 0 or above. Attic and technical floors count; floors below the entrance, mezzanines and the roof do not. Null when `storey_stack` is empty.',
  ),
  mass_composition_note: nullableStr(
    'One short sentence describing how the masses sit together when there is more than one (vertical stacking with differing footprints, off-axis stacking, side-by-side masses tied together, etc.); null when the building is a single mass.',
  ),
  physical_attachments: z
    .array(
      z.object({
        other_building_label: z
          .string()
          .describe(
            'In-document name of the OTHER building this one is physically joined to (e.g. "Hus 3"). Echo what the drawings call it.',
          ),
        shared_structure: z
          .string()
          .describe(
            'Short prose describing what is physically shared between the two buildings, in the words the drawings use.',
          ),
        should_model_as_single_building: z
          .boolean()
          .describe(
            'true when the two volumes are joined into one continuous building body and nodon should model them as ONE building. false when they are structurally separate volumes that only touch or are linked by a non-structural element.',
          ),
      }),
    )
    .describe(
      "Cross-building adjacency notes drawn from THIS group's own files. Drawings often show neighbouring buildings on the same sheet (site plan, joint section), so each group can self-report adjacency without seeing the other group's files. Empty array when this building is freestanding.",
    ),
});
export type JoinedBlueprintBuilding = z.infer<
  typeof joinedBlueprintBuildingSchema
>;

export const joinedBlueprintExtractionRootSchema = z.object({
  buildings: z
    .array(joinedBlueprintBuildingSchema)
    .describe(
      'Exactly one row for the building group covered by this call. Use a single-row array — the caller dispatches one call per group.',
    ),
});
export type JoinedBlueprintExtractionRoot = z.infer<
  typeof joinedBlueprintExtractionRootSchema
>;

export const PROJECT_FILE_BLUEPRINT_JOINED_EXTRACTION_SCHEMA_NAME =
  'project_file_blueprint_joined_extraction' as const;

export const buildJoinedBlueprintExtractionRootJsonSchema = (): Record<
  string,
  unknown
> => stripJsonSchemaMeta(joinedBlueprintExtractionRootSchema);
